import { DateTime } from 'luxon';
import { isEqual } from 'lodash';
import { Admin } from '../admin/admin';

export enum JobType {
  CheckoutInspection = 'CheckoutInspection',
  CheckinInspection = 'CheckinInspection',
}

export enum TaskType {
  InspectionChecklist = 'InspectionChecklist',
  MediaUpload = 'MediaUpload',
  MachineHours = 'MachineHours',
}

export type TaskConfig = {
  // task configs as individual entities separate from any job configs
  id: string;
  type: TaskType;
  header: string;
  instructions?: string;
  metadata?: Record<string, any>;
  enabled: boolean;
  required: boolean;
  updatedAt: DateTime;
  updatedBy: number;
  version: number;
};

export type TaskConfigRequest = Omit<TaskConfig, 'updatedAt' | 'updatedBy' | 'version' | 'id'>;

export function equalTaskConfigs(a: TaskConfig, b: TaskConfig): boolean {
  return (
    a.type === b.type &&
    a.header === b.header &&
    a.instructions === b.instructions &&
    isEqual(a.metadata, b.metadata) &&
    a.enabled === b.enabled &&
    a.required === b.required
  );
}

export type JobConfig = {
  id: string;
  jobType: JobType;
  name: string;
  taskConfigs: TaskConfig[];

  products?: number[];
  categories?: number[];
};

export type JobConfigRequest = Omit<JobConfig, 'id' | 'taskConfigs'> & { taskConfigs: TaskConfigRequest[] };

export type ChecklistItem = {
  id?: string;
  text: string;
  isBoolean: boolean; // if true, yes/no answer; else just check off as completed
  value?: boolean;
  required: boolean;
  updatedAt?: DateTime;
  updatedBy?: number;
  notes?: string;
  media: string[];
};

/********************** end job config stuff *************** */

export type JobStatus = 'NotStarted' | 'InProgress' | 'Complete';
export type JobEntityType = 'Order';

export class Job {
  readonly id!: string;
  readonly type!: JobType;
  readonly entityType!: JobEntityType;
  readonly entityId!: string;
  readonly status!: JobStatus;
  readonly createdAt!: DateTime;
  readonly updatedAt!: DateTime;
  readonly updatedBy!: number;
  readonly tasks!: Task[];
}

export class TaskRowInsert {
  readonly id!: string;
  readonly jobId!: string;
  readonly taskConfigId!: string;
  readonly taskConfigVersion!: number;
  readonly step!: number;
  readonly status!: JobStatus;
  readonly metadata?: Record<string, any>;
  readonly startedAt?: DateTime;
  readonly completedAt?: DateTime;
  readonly completedBy?: number;
  // readonly deletedAt?: DateTime; // if task was started but then product unit was unassigned from order..?
}

export class TaskRow extends TaskRowInsert {
  readonly type!: TaskType;
  readonly header!: string;
  readonly instructions?: string;
  readonly required!: boolean;
}

export class Task {
  readonly id!: string;
  readonly jobId!: string;
  readonly type!: TaskType;
  readonly taskConfigId!: string;
  readonly taskConfigVersion!: number;
  readonly header!: string;
  readonly instructions?: string;
  readonly status!: JobStatus;
  readonly metadata?: Record<string, any>; // TODO: this doesn't need to be arbitrary json, we should have specific types for each task type
  readonly startedAt?: DateTime;
  readonly completedAt?: DateTime;
  readonly completedBy?: Admin;
}

export type UpdateTaskRequest = {
  status: JobStatus;
  metadata: Record<any, any> &
    ({ checklist: ChecklistItem[] } | { media: { [section: string]: string[] } } | { hours: number; fuel: number });
};

/*
  - The majority of this inspection metadata is arbitrary, so here's some info to help parse it:
*/
export interface MachineHoursMetadata {
  fuel: number;
  hours: number;
  media: string[];
  orderId: number;
  productId: number;
  productUnitId: number;
}
